'use client'

import React, { useState, useEffect, useMemo, useRef, createContext, useContext, ReactNode, FC } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import axios from 'axios'
import { useAuthContext } from './AuthContext'
import { getUserToken } from '@/lib/utils/misc'
import { IBar } from '@/lib/interfaces/bar'
import { IMenu } from '@/lib/interfaces/interfaces'

interface BarDataContextType {
  barData: IBar | null;
  isDeliveryReady: boolean;
  refreshBarData: (showLoading: boolean) => Promise<void>;
  loadingBarData: boolean
  barDataLoadError: boolean
  setBarData: React.Dispatch<React.SetStateAction<IBar | null>>
}

export type { BarDataContextType }

const BarDataContext = createContext<BarDataContextType>({
  barData: null,
  isDeliveryReady: false,
  refreshBarData: async () => {},
  loadingBarData: false,
  barDataLoadError: false,
  setBarData: () => {}
})

export { BarDataContext }

export const useBarDataContext = (): BarDataContextType => useContext(BarDataContext)

interface BarDataContextProviderProps {
  children: ReactNode;
}

const ONBOARDING_PATH = '/onboarding'

const isPathInside = (pathname: string, segment: string): boolean => {
  const segments = pathname.split('/').filter(Boolean)
  return segments.includes(segment)
}

export const BarDataContextProvider: FC<BarDataContextProviderProps> = ({ children }) => {
  const { user } = useAuthContext()
  const router = useRouter()
  const pathname = usePathname()
  const [barData, setBarData] = useState<IBar | null>(null)
  const [loadingBarData, setLoadingData] = useState(true)
  const [barDataLoadError, setBarDataLoadError] = useState(false)
  const pendingTrackedUid = useRef<string | null>(null)

  const refreshBarData = async (showLoading: boolean) => {
    if (showLoading) setLoadingData(true)
    setBarDataLoadError(false)

    if (!user) {
      setBarData(null)
      setLoadingData(false)
      return
    }

    try {
      const token = await getUserToken(user)
      const response = await axios.get('/api/bar-data', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      })

      const data: IBar = response.data
      if (data && data.menus && data.menus[0] && (data.menus[0] as IMenu).sections) {
        setBarData(data)
        setLoadingData(false)
        return
      }

      // Defensa contra bares incompletos / corruptos (menus vacío o sin
      // sections). Si la API devolvió un objeto bar —aunque esté
      // incompleto—, lo aceptamos como "el usuario tiene bar" y NO
      // redirigimos a /onboarding. Esto rompe el loop potencial:
      //   /dashboard → barData=null → /onboarding → submit → alreadyExists
      //   → /dashboard → barData=null → /onboarding → ...
      // En el caso raro de un bar realmente roto, el dashboard tendrá que
      // mostrar un fallback (sección vacía, mensaje de error, etc.), pero
      // al menos el usuario no queda atrapado en un bucle de redirects.
      // El caso "API devolvió null" (no hay bar en absoluto) sigue
      // cayendo al setBarData(null) de abajo, que sí redirige.
      if (data) {
        setBarData(data)
        setLoadingData(false)
        return
      }
    } catch (error) {
      // Solo el 404 confirma que no hay Bar. Un error de red, token o servidor
      // no debe convertir una sesión existente en un onboarding nuevo.
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      if (status !== 404) {
        console.error('Error al recuperar la info del usuario en la BD', error)
        setBarDataLoadError(true)
        setLoadingData(false)
        return
      }
    }

    setBarData(null)
    setLoadingData(false)
  }

  useEffect(() => {
    refreshBarData(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (!user || loadingBarData || barDataLoadError) return
    if (barData) return

    // No redirigir si ya estamos en una ruta de onboarding / login / signup.
    if (isPathInside(pathname, 'onboarding')) return
    if (isPathInside(pathname, 'login')) return
    if (isPathInside(pathname, 'signup')) return

    // Rescate pre-Bar: el usuario va a rebotar a /onboarding sin bar.
    // Registramos el pendiente (una vez por uid, fire-and-forget).
    if (user.uid && pendingTrackedUid.current !== user.uid) {
      pendingTrackedUid.current = user.uid
      const locale = pathname.split('/').filter(Boolean)[0] === 'en' ? 'en' : 'es'
      getUserToken(user)
        .then((token) => fetch('/api/onboarding-tracking', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            email: user.email ?? null,
            displayName: user.displayName ?? null,
            providerId: user.providerData?.[0]?.providerId ?? null,
            locale
          })
        }))
        .catch((err) => console.error('Onboarding tracking failed', err))
    }

    router.push(ONBOARDING_PATH)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, barData, loadingBarData, barDataLoadError, pathname])

  const isDeliveryReady = useMemo(
    () => barData?.stripeAccountConnect?.onboardingStatus === 'completed',
    [barData?.stripeAccountConnect?.onboardingStatus]
  )

  return (
    <BarDataContext.Provider value={{ barData, isDeliveryReady, refreshBarData, loadingBarData, barDataLoadError, setBarData }}>
      {children}
    </BarDataContext.Provider>
  )
}
